import React from 'react'

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))

const formatDuration = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—'
  const rounded = Math.round(seconds)
  const minutes = Math.floor(rounded / 60)
  const remainingSeconds = rounded % 60
  if (minutes <= 0) return `${remainingSeconds}s`
  return `${minutes}m ${String(remainingSeconds).padStart(2, '0')}s`
}

type ExportProgress = {
  loaded: number
  total: number
}

type ExportMetricsResult = {
  hasFirstResponse: boolean
  percent: number
  title: string
  subtitle: string
  details: string
}

const useExportMetrics = (open: boolean, progress: ExportProgress): ExportMetricsResult => {
  const startedAtRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    if (!open) {
      startedAtRef.current = null
      return
    }

    if (!startedAtRef.current) {
      startedAtRef.current = Date.now()
    }
  }, [open])

  const hasFirstResponse = progress.loaded > 0

  const { percent, title, subtitle, details } = React.useMemo(() => {
    const { loaded, total } = progress
    const startedAt = startedAtRef.current ?? Date.now()
    const elapsedSec = (Date.now() - startedAt) / 1000

    const percentValue = total ? clamp((loaded / total) * 100, 0, 100) : 0
    const speed = loaded > 0 && elapsedSec > 0 ? loaded / elapsedSec : 0
    const remaining = total ? Math.max(total - loaded, 0) : 0
    const etaSec = total && speed > 0 ? remaining / speed : NaN

    let computedTitle = 'Export des données'
    if (total && loaded >= total) {
      computedTitle = 'Finalisation de l’export…'
    }

    let computedSubtitle = 'Chargement des véhicules...'
    if (total) {
      computedSubtitle = `${loaded} / ${total} véhicules (${Math.round(percentValue)}%)`
    } else if (loaded) {
      computedSubtitle = `${loaded} véhicules chargés…`
    }

    let computedDetails = ' '
    if (total) {
      computedDetails = `Vitesse ~ ${speed.toFixed(1)} vh/s • Temps restant ~ ${formatDuration(etaSec)}`
    } else if (speed) {
      computedDetails = `Vitesse ~ ${speed.toFixed(1)} vh/s`
    }

    return {
      percent: percentValue,
      title: computedTitle,
      subtitle: computedSubtitle,
      details: computedDetails,
    }
  }, [progress])

  return { hasFirstResponse, percent, title, subtitle, details }
}

export default useExportMetrics
